import { FC } from "react";
import { useParams } from "react-router-dom";
import { AllNewsItem } from "./NewsCardErrorWrapper";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { Box, Callout, Grid, Theme } from "@radix-ui/themes";
import { Loader, OverviewArchive } from "@/components";
import Cover from "@/assets/cover/news.webp";
import i18next from "i18next";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useTenantNews } from "../../hooks/useTenantNews";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import useSetClient from "@/hooks/useSetClient";
import { themeMap, themeRadiusMap } from "@/utils/constants/Themes";

export const NewsOverview: FC = (): JSX.Element => {
  const { slug } = useParams();
  useDocumentTitle(i18next.t("news.headline.default"));
  const coverImage = brand?.cover?.news ? brand.cover.news : Cover;

  const { zoneItem, loadingZone } = useSetClient(slug);
  const theme =
    themeMap[zoneItem?.theme.toUpperCase()] ||
    themeMap[brand.brandTheme.toUpperCase()] ||
    "violet";
  const radius = themeRadiusMap[zoneItem?.theme.toUpperCase()] || "none";

  const { data, loading, error } = useTenantNews();

  const sortedData = data?.slice().sort((a, b) => {
    const dateA = a.created.toDate();
    const dateB = b.created.toDate();
    return dateB.getTime() - dateA.getTime();
  });

  if (loadingZone) {
    return <Loader />;
  }

  if (slug) {
    const zoneData = sortedData?.filter((news) => news.zone === slug);

    return (
      <>
        <Theme appearance="dark" accentColor={theme} radius={radius}>
          <OverviewArchive
            headline={`${zoneItem.title} ${i18next.t("news.headline.default")}`}
            subtitle={`${i18next.t("news.desc")} ${zoneItem.title}.`}
            disableBadges={loading}
            background={coverImage}
          />
          {error && (
            <Box>
              <Callout.Root color="red">
                <Callout.Icon>
                  <ExclamationTriangleIcon />
                </Callout.Icon>
                <Callout.Text>{error.message}</Callout.Text>
              </Callout.Root>
            </Box>
          )}
          <Grid
            mt="3"
            columns={{ initial: "1", sm: "2", md: "3", xl: "4" }}
            gap="3"
          >
            {zoneData &&
              zoneData.map((news) => {
                return (
                  <Box minHeight="100%" key={news.slug}>
                    {AllNewsItem(news, loading, zoneItem.slug)}
                  </Box>
                );
              })}
          </Grid>
        </Theme>
      </>
    );
  }

  return (
    <>
      <OverviewArchive
        headline={i18next.t("news.headline.default")}
        subtitle={`${i18next.t("news.desc")} ${brand.name}.`}
        disableBadges={loading}
        background={coverImage}
      />
      {error && (
        <Box>
          <Callout.Root color="red">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>{error.message}</Callout.Text>
          </Callout.Root>
        </Box>
      )}
      <Grid
        mt="3"
        columns={{ initial: "1", sm: "2", md: "3", xl: "4" }}
        gap="3"
      >
        {sortedData &&
          sortedData.map((news) => {
            return (
              <Box minHeight="100%" key={news.slug}>
                {AllNewsItem(news, loading)}
              </Box>
            );
          })}
      </Grid>
    </>
  );
};

export default NewsOverview;
